import { useMutation, useQuery } from "@tanstack/react-query"
import { ProductType } from "@tfg/db"
import { AxiosError } from "axios"

import {
  EcobalyseCountry,
  EcobalyseMaterialType,
} from "@tfg/types/endpoints/analysis"
import { LibraryResponses } from "@tfg/types/endpoints/library"
import { componentUpdateNameValidationType } from "@tfg/types/schemas/components"
import { materialUpdateValidationType } from "@tfg/types/schemas/materials"
import { updateAgecShowValidationType } from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"

export const useProductQuery = (productId?: string) => {
  return useQuery<LibraryResponses.ProductDetails>({
    queryKey: ["products", { productId }],
    queryFn: async () => {
      const response = await httpClient.get(`products/${productId}`)
      return response.data
    },
    enabled: Boolean(productId),
  })
}

export const useUpdateAgecShowMutation = (productId: number) => {
  return useMutation<unknown, unknown, updateAgecShowValidationType>({
    mutationKey: ["products", productId],

    mutationFn: async (variables) => {
      const response = await httpClient.patch(
        `products/agec-show/${productId}`,
        variables
      )
      return response.data
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["products", { productId }],
      })
    },
  })
}

export const useProductTypesQuery = () => {
  return useQuery<ProductType[]>({
    queryKey: ["productTypes"],

    queryFn: async () => {
      const response = await httpClient.get(`products/types`)
      return response.data
    },
  })
}

export const useComponentUpdateNameMutation = () =>
  useMutation<void, AxiosError, componentUpdateNameValidationType>({
    mutationFn: async (variables) => {
      const response = await httpClient.patch(`components/name`, variables)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["products"] })
    },
  })

export const useUpdateMaterialMutation = () =>
  useMutation<void, AxiosError, materialUpdateValidationType>({
    mutationFn: async (variables) => {
      const response = await httpClient.patch(`materials`, variables)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["products"],
      })
    },
  })

export const useListEcobalyseMaterialTypesQuery = () =>
  useQuery({
    queryKey: ["ecobalyse", "materialTypes"],

    queryFn: async () => {
      const res = await httpClient.get<EcobalyseMaterialType[]>(
        "/analysis/materialTypes"
      )

      return res.data
    },
  })

export const useListEcobalyseSupportedCountriesQuery = () =>
  useQuery({
    queryKey: ["ecobalyse", "countries"],

    queryFn: async () => {
      const res = await httpClient.get<EcobalyseCountry[]>(
        "/analysis/countries"
      )

      return res.data
    },
  })
